import { useState } from 'react';
import { FieldError, useForm } from 'react-hook-form';
import { Box, Button, Stack } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';

import { IAddressPrediction, mapFullAddressFromCaseDetailsAddress } from 'api';
import { EditIcon } from 'components/v2/icons/EditIcon';
import AutoCompleteInput from 'components/v2/input/AutoCompleteInput';
import { InputField } from 'components/v2/input/InputField';
import Modal from 'components/v2/modal/Modal';
import { DEBOUNCE_TIME } from 'constants/server/apiSettings';
import { MODALS_STORE } from 'constants/ui/modal';
import { LAYOUT_SPACING } from 'constants/ui/style';
import { useDebounce } from 'hooks/general/useDebounce';
import { useLocalization } from 'localization';
import { editAddressSchema } from 'components/v2/division/form/schema/createOrderSchema';
import { getAddressSuggestionsApi } from 'api/address/query';
import { NumberInputField } from 'components/v2/input/NumberInputField';
import { getCoordinate } from 'helpers/getCoordinate';

export type AddressFormValues = {
  fullAddress?: string;
  street: string;
  houseNumber: string;
  postNumber: string;
  city: string;
  area?: string;
  appartmentNumber?: string;
  floor?: string;
  longitude?: number;
  latitude?: number;
};

type Props = {
  onAddAddress: (address?: Partial<AddressFormValues>) => void;
  modalId: string;
  error?: FieldError;
  defaultValue?: AddressFormValues;
};

export const initialEditAddressValue = {
  street: '',
  houseNumber: '',
  postNumber: '',
  city: '',
  area: '',
  appartmentNumber: '',
  floor: '',
};

const CreateNewOrderEditAddress = (props: Props) => {
  const { onAddAddress, modalId, error, defaultValue } = props;
  const { t } = useLocalization();
  const { toggleModal, isModalOpen } = MODALS_STORE.createOrder();
  const [editAddressKey, setEditAddressKey] = useState(1);

  const { control, setValue, watch, trigger, getValues, handleSubmit } = useForm({
    resolver: yupResolver(editAddressSchema()),
    defaultValues: defaultValue,
    values: defaultValue,
  });

  const debouncedSearchAddress = useDebounce(watch('fullAddress') ?? '', DEBOUNCE_TIME);
  const { data, isLoading } = getAddressSuggestionsApi({ addressTextSearch: debouncedSearchAddress });

  const handleAddressSelection = (value: string, suggestionData: IAddressPrediction[] | undefined) => {
    const internalAddress = suggestionData?.find(suggestion => suggestion.text.includes(value))?.__internal_address;
    if (internalAddress) {
      setValue('fullAddress', internalAddress.full);
      setValue('street', internalAddress.street);
      setValue('houseNumber', internalAddress.houseNumber);
      setValue('postNumber', internalAddress.postNumber);
      setValue('city', internalAddress.city);
      setValue('appartmentNumber', internalAddress.appartmentNumber ?? '');
      setValue('floor', internalAddress.floor ?? '');
      setValue('area', internalAddress.area ?? '');
      setValue('longitude', internalAddress.longitude);
      setValue('latitude', internalAddress.latitude);

      onAddAddress(getValues());
    }
  };

  const handleSaveAddress = async () => {
    const isValid = await trigger();
    if (isValid) {
      const addressFull = mapFullAddressFromCaseDetailsAddress({
        ReferenceAddressStreet: watch('street') ?? '',
        ReferenceAddressHouseNumber: watch('houseNumber') ?? '',
        ReferenceAddressFloor: watch('floor') ?? '',
        ReferenceAddressDoor: watch('appartmentNumber') ?? '',
        ReferenceAddressZip: watch('postNumber') ?? '',
        ReferenceAddressCity: watch('city') ?? '',
      });
      setValue('fullAddress', addressFull);

      await getCoordinate(addressFull).then(res => {
        setValue('longitude', res?.longitude);
        setValue('latitude', res?.latitude);
      });

      onAddAddress(getValues());
      toggleModal(modalId);
      setEditAddressKey(prevKey => prevKey + 1); // Increment key to force re-render of AutoCompleteInput
    }
  };

  return (
    <Box>
      <Stack
        direction="row"
        gap={1.25}
        justifyContent={'space-between'}
        alignItems={error !== undefined ? 'center' : 'flex-end'}
      >
        <Box sx={{ flex: 3 }}>
          <AutoCompleteInput
            key={editAddressKey}
            options={
              data?.map((suggestion: IAddressPrediction) => ({
                value: suggestion.text,
                label: suggestion.text,
              })) ?? []
            }
            label={'common.terms.address'}
            isLoading={isLoading}
            name="fullAddress"
            placeholder={watch('fullAddress') ?? 'common.terms.searchAddress'}
            control={control}
            required
            searchValue={watch('fullAddress')}
            onInputChange={(_, inputValue) => {
              setValue('fullAddress', inputValue);
              handleAddressSelection(inputValue, data);
            }}
            otherError={error}
          />
        </Box>
        <Button
          variant="outlined"
          size="medium"
          sx={{ width: 'fit-content', flex: 1 }}
          startIcon={<EditIcon />}
          onClick={() => toggleModal(modalId)}
        >
          {t('common.actions.editManually')}
        </Button>
      </Stack>
      {isModalOpen(modalId) && (
        <Modal
          modalTitle={t('modals.editAddress.title')}
          isOpen={isModalOpen(modalId)}
          toggleModal={() => toggleModal(modalId)}
          onConfirm={handleSubmit(handleSaveAddress)}
          confirmBtnLabel={t('common.actions.save')}
        >
          <Stack spacing={LAYOUT_SPACING.FORM.xs}>
            <InputField control={control} name="street" label="common.terms.street" required />
            <NumberInputField control={control} name="houseNumber" label="common.terms.houseNumber" required min={0} />
            <NumberInputField control={control} name="postNumber" label="common.terms.postNumber" required min={0} />
            <InputField control={control} name="city" label="common.terms.city" required />
            <InputField control={control} name="floor" label="common.terms.floor" />
            <InputField control={control} name="appartmentNumber" label="common.terms.appartmentNumber" />
          </Stack>
        </Modal>
      )}
    </Box>
  );
};

export default CreateNewOrderEditAddress;
